import type React from "react";
import { Group, Loader, Stack, Text } from "@mantine/core";
import { Badge, Table } from "@xplatform/design-system";
import { IconCornerDownRight, IconStarFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { RowEdit } from "@models/job";
import type { MappingResultRowOe, OeTarget } from "@models/mappingResult";
import { buildCandidates } from "@utils/rowHelpers";
import AltText from "@components/AltText/AltText";

interface WdJobResultsExpandedRowProps {
  row: MappingResultRowOe;
  edit: RowEdit | undefined;
  /** Every candidate the server served for this row, rank 1 first. */
  candidates: readonly OeTarget[] | undefined;
  colSpan: number;
  disabled?: boolean;
  /** True while the candidates are still being fetched on demand. */
  loading?: boolean;
  onPickAlternative: (rank: number) => void;
}

/**
 * The candidate dropdown for one OE row.
 *
 * Rendered exactly as the server sent it. Three things this used to do to the
 * list are gone, and each was hiding something the backend meant to serve:
 *
 *  - `slice(1, …)` dropped rank 1. Under the old API that was the row's top
 *    match, shown above; under this one the alternatives endpoint returns rank
 *    1 FIRST on purpose, because after a correction it is the only way back.
 *    On a catalogue miss it is the model's proposal, so dropping it removed
 *    the reviewer's way of saying "no, it really is missing".
 *  - a client-side cap of 3, while the backend serves up to 10.
 *  - a dedupe by `item_mp_id`, which could drop a legitimate second candidate.
 *
 * `isCurrent` marks the row's current answer; nothing here compares a rank
 * to 1.
 */
const WdJobResultsExpandedRow: React.FC<WdJobResultsExpandedRowProps> = ({
  row,
  edit,
  candidates,
  colSpan,
  disabled = false,
  loading = false,
  onPickAlternative,
}) => {
  const { t } = useTranslation();
  const entries = buildCandidates(row, edit, candidates);

  if (entries.length === 0) {
    return (
      <Table.Tr className="job-results-page__alts-empty-row">
        <Table.Td colSpan={colSpan}>
          {loading ? (
            <Group justify="center" gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                {t("jobResultsPage.expand.loading", {
                  defaultValue: "Loading matches…",
                })}
              </Text>
            </Group>
          ) : (
            <Text size="sm" c="dimmed" ta="center">
              {t("jobResultsPage.expand.empty", {
                defaultValue: "No matches available",
              })}
            </Text>
          )}
        </Table.Td>
      </Table.Tr>
    );
  }

  const handlePick = (rank: number): void => {
    if (disabled) return;
    onPickAlternative(rank);
  };

  return (
    <>
      <Table.Tr className="job-results-page__alts-header-row">
        <Table.Td colSpan={colSpan}>
          <Text
            size="xs"
            fw={700}
            c="teal"
            tt="uppercase"
            style={{ letterSpacing: "0.06em" }}
          >
            {t("jobResultsPage.expand.title", {
              count: entries.length,
              defaultValue: `Top ${entries.length} matches - click to pick`,
            })}
          </Text>
        </Table.Td>
      </Table.Tr>
      {entries.map(({ target, isCurrent }) => {
        const cls = `job-results-page__alt-row${
          isCurrent ? " job-results-page__alt-row--selected" : ""
        }${disabled ? " job-results-page__alt-row--disabled" : ""}`;
        return (
          <Table.Tr
            key={target.rank ?? target.itemMpId}
            className={cls}
            onClick={() => target.rank != null && handlePick(target.rank)}
            style={
              disabled
                ? { cursor: "wait", pointerEvents: "none", opacity: 0.5 }
                : undefined
            }
          >
            <Table.Td>
              <IconCornerDownRight
                size={14}
                className="job-results-page__alt-arrow"
              />
            </Table.Td>
            <Table.Td>
              <Badge size="sm" variant="light" color="gray" radius="sm">
                #{target.rank}
              </Badge>
            </Table.Td>
            {/* Source columns — blank, so a candidate lines up under the
                suggestion columns it belongs to. */}
            <Table.Td />
            <Table.Td />
            <Table.Td />
            <Table.Td>
              <AltText
                value={target.itemMpId == null ? null : String(target.itemMpId)}
                mono
              />
            </Table.Td>
            <Table.Td>
              {target.isProposal ? (
                // Labelled, because in a list of catalogue candidates a name
                // with no id beside it is otherwise just an odd-looking row.
                <Stack gap={2}>
                  <Text size="sm" ta="left" lineClamp={2} fs="italic">
                    {target.suggestedText}
                  </Text>
                  <Badge
                    size="xs"
                    variant="light"
                    color="violet"
                    leftSection={<IconStarFilled size={10} />}
                  >
                    {t("jobResultsPage.aiSuggested", {
                      defaultValue: "AI suggested",
                    })}
                  </Badge>
                </Stack>
              ) : (
                <AltText value={target.itemMpText} />
              )}
            </Table.Td>
            <Table.Td>
              {isCurrent && (
                <Badge size="xs" variant="filled" color="teal">
                  {t("jobResultsPage.expand.selected", {
                    defaultValue: "Selected",
                  })}
                </Badge>
              )}
            </Table.Td>
            <Table.Td />
            <Table.Td />
          </Table.Tr>
        );
      })}
    </>
  );
};

export default WdJobResultsExpandedRow;
